import { useState, useEffect } from "react";
import { dateLocale } from "../lib/languages";
import axios from "axios";
import { toast } from "sonner";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Slider } from "../components/ui/slider";
import { Textarea } from "../components/ui/textarea";
import { Heart, TrendingUp, Calendar, Plus, Check } from "lucide-react";
import { API } from "../App";
import ExerciseIntro from "../components/ExerciseIntro";
import { getExerciseInfo } from "../lib/exerciseData";
import { useI18n } from "../lib/i18n";

const MoodTracker = ({ user, logout }) => {
  const { locale, t } = useI18n();
  const EXERCISE_INFO = getExerciseInfo(t);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [moodLevel, setMoodLevel] = useState([5]);
  const [selectedEmotions, setSelectedEmotions] = useState([]);
  const [notes, setNotes] = useState("");
  const [customEmotion, setCustomEmotion] = useState("");

  const EMOTIONS = [
    { id: "happy", label: t("emotion.happy"), emoji: "\u{1F60A}" },
    { id: "calm", label: t("emotion.calm"), emoji: "\u{1F60C}" },
    { id: "anxious", label: t("emotion.anxious"), emoji: "\u{1F630}" },
    { id: "sad", label: t("emotion.sad"), emoji: "\u{1F622}" },
    { id: "angry", label: t("emotion.angry"), emoji: "\u{1F620}" },
    { id: "stressed", label: t("emotion.stressed"), emoji: "\u{1F613}" },
    { id: "hopeful", label: t("emotion.hopeful"), emoji: "\u{1F31F}" },
    { id: "tired", label: t("emotion.tired"), emoji: "\u{1F634}" },
    { id: "grateful", label: t("emotion.grateful"), emoji: "\u{1F64F}" },
    { id: "frustrated", label: t("emotion.frustrated"), emoji: "\u{1F624}" },
    { id: "peaceful", label: t("emotion.peaceful"), emoji: "\u{262E}\u{FE0F}" },
    { id: "motivated", label: t("emotion.motivated"), emoji: "\u{1F4AA}" },
  ];

  useEffect(() => {
    fetchEntries();
  }, []);

  const fetchEntries = async () => {
    try {
      const response = await axios.get(`${API}/mood`, { withCredentials: true });
      setEntries(response.data);
    } catch (error) {
      console.error("Error fetching mood entries:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (selectedEmotions.length === 0) {
      toast.error(t("mood.selectEmotion"));
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/mood`, {
        mood_level: moodLevel[0],
        emotions: selectedEmotions,
        notes: notes || null,
      }, { withCredentials: true });

      toast.success(t("mood.recorded"));
      setMoodLevel([5]);
      setSelectedEmotions([]);
      setNotes("");
      setCustomEmotion("");
      fetchEntries();
    } catch (error) {
      toast.error(t("mood.errorRecording"));
    } finally {
      setSubmitting(false);
    }
  };

  const toggleEmotion = (emotionId) => {
    setSelectedEmotions(prev => 
      prev.includes(emotionId)
        ? prev.filter(e => e !== emotionId)
        : [...prev, emotionId]
    );
  };

  const addCustomEmotion = () => {
    const trimmed = customEmotion.trim();
    if (trimmed && !selectedEmotions.includes(trimmed)) {
      setSelectedEmotions(prev => [...prev, trimmed]);
      setCustomEmotion("");
    }
  };

  const getMoodColor = (level) => {
    if (level <= 3) return "text-rose-500";
    if (level <= 5) return "text-amber-500";
    if (level <= 7) return "text-lime-500";
    return "text-emerald-500";
  };

  const getMoodLabel = (level) => {
    if (level <= 2) return t("mood.veryBadLabel");
    if (level <= 4) return t("mood.notGood");
    if (level <= 6) return t("mood.okay");
    if (level <= 8) return t("mood.good");
    return t("mood.veryGoodLabel");
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat(dateLocale(locale), {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="space-y-8" data-testid="mood-tracker">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold">{t("mood.title")}</h1>
          <p className="text-muted-foreground text-sm sm:text-base mt-1">{t("mood.subtitle")}</p>
        </div>

        <ExerciseIntro exerciseInfo={EXERCISE_INFO.mood_tracking} />

        <div className="grid lg:grid-cols-2 gap-8">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-xl flex items-center gap-2">
                <Plus className="w-5 h-5" />
                {t("exercise.newEntry")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">{t("mood.howFeel")}</span>
                  <span className={`text-2xl font-semibold ${getMoodColor(moodLevel[0])}`}>
                    {moodLevel[0]}/10
                  </span>
                </div>
                <Slider value={moodLevel} onValueChange={setMoodLevel} min={1} max={10} step={1} className="w-full" data-testid="mood-slider" />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{t("mood.veryBad")}</span>
                  <span className={`font-medium ${getMoodColor(moodLevel[0])}`}>{getMoodLabel(moodLevel[0])}</span>
                  <span>{t("mood.veryGood")}</span>
                </div>
              </div>

              <div className="space-y-3">
                <span className="text-sm text-muted-foreground">{t("mood.whatEmotions")}</span>
                <div className="flex flex-wrap gap-2">
                  {EMOTIONS.map((emotion) => (
                    <button key={emotion.id} onClick={() => toggleEmotion(emotion.id)}
                      className={`px-3 py-2 rounded-full text-sm transition-all border ${selectedEmotions.includes(emotion.id) ? 'bg-primary text-white border-primary' : 'bg-background text-foreground border-border hover:bg-muted'}`}
                      data-testid={`emotion-${emotion.id}`}>
                      {emotion.emoji} {emotion.label}
                    </button>
                  ))}
                  {selectedEmotions.filter(e => !EMOTIONS.find(em => em.id === e)).map(custom => (
                    <button key={custom} onClick={() => toggleEmotion(custom)}
                      className="px-3 py-2 rounded-full text-sm transition-all border bg-primary text-white border-primary"
                      data-testid={`emotion-custom-${custom}`}>{custom}</button>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input type="text" placeholder={t("mood.otherEmotion")} value={customEmotion}
                    onChange={(e) => setCustomEmotion(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCustomEmotion())}
                    className="flex-1 px-3 py-2 text-sm border rounded-full bg-background border-border focus:outline-none focus:ring-2 focus:ring-primary/30"
                    data-testid="custom-emotion-input" />
                  <Button variant="outline" size="sm" onClick={addCustomEmotion} disabled={!customEmotion.trim()} className="rounded-full" data-testid="add-custom-emotion-btn">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <span className="text-sm text-muted-foreground">{t("mood.notes")}</span>
                <Textarea placeholder={t("mood.notesPlaceholder")} value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} data-testid="mood-notes" />
              </div>

              <Button onClick={handleSubmit} disabled={submitting} className="w-full bg-primary hover:bg-primary/90" data-testid="submit-mood-btn">
                {submitting ? t("exercise.saving") : t("mood.record")}
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-xl flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                {t("common.recentHistory")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-4">{[1, 2, 3].map((i) => (<div key={i} className="animate-pulse h-20 bg-muted rounded-lg" />))}</div>
              ) : entries.length > 0 ? (
                <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2">
                  {entries.map((entry) => (
                    <div key={entry.entry_id} className="p-4 border rounded-lg hover:bg-muted/50 transition-colors">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Heart className={`w-5 h-5 ${getMoodColor(entry.mood_level)}`} />
                          <span className={`font-semibold ${getMoodColor(entry.mood_level)}`}>{entry.mood_level}/10</span>
                        </div>
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <Calendar className="w-3.5 h-3.5" />{formatDate(entry.created_at)}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1 mb-2">
                        {entry.emotions.map((e) => {
                          const emotion = EMOTIONS.find(em => em.id === e);
                          return (<span key={e} className="text-xs px-2 py-1 bg-muted rounded-full">{emotion ? `${emotion.emoji} ${emotion.label}` : e}</span>);
                        })}
                      </div>
                      {entry.notes && (<p className="text-sm text-muted-foreground line-clamp-2">{entry.notes}</p>)}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Heart className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">{t("common.noEntriesYet")}</p>
                  <p className="text-sm text-muted-foreground text-sm sm:text-base mt-1">{t("mood.startTracking")}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default MoodTracker;
